// NemAnalysisStepper.tsx
// Site page "NEM Analysis" tab: human-in-the-loop stepped pipeline over
// GCS-synced meter data.
//
//   Step 1 — Interval Data      coverage / missing days / channels / quality
//   Step 2 — Network Tariff     every configured bucket, priced (TariffTab)
//   Step 3 — Retail TOU         peak/shoulder/off-peak, priced from the contract
//   Step 4 — Shadow Bill        apply results to a bill + recalculate shadow
//
// Nothing auto-advances: every step is a button, results reviewed before the
// next click. Each step shows its own error. Re-running a step resets the ones
// after it (server-enforced).

import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  AlertTriangle, CheckCircle2, ChevronDown, ChevronUp, Database,
  Loader2, Network, PlayCircle, RefreshCw, Timer, Upload, X, Zap,
} from 'lucide-react';
import {
  Card, CardContent, CardHeader,
} from '../../../foundation/altzero_base/base/components/ui/card';
import { Button } from '../../../foundation/altzero_base/base/components/ui/button';
import { Input } from '../../../foundation/altzero_base/base/components/ui/input';
import { Label } from '../../../foundation/altzero_base/base/components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../../foundation/altzero_base/base/components/ui/select';
import { cn } from '../../../foundation/altzero_base/base/lib/utils';
import { nemAnalysisService } from '../services/nemAnalysisService';
import { billService } from '../services/billService';
import { usageService } from '../services/usageService';
import { TouCalculationCard } from './TouCalculationCard';
import { rollupRetailBands } from '../utils/retailBandRollup';
import { lookbackFrom } from '../utils/demandLookback';
import type { NemAnalysisRun, NemStepStatus, StreamCoverage } from '../types/nemAnalysisTypes';
import type { TouByNmi } from '../types/nemTypes';
import type { BvBill } from '../types/billTypes';
import type {
  BvDistributionTariffUsage, BvRetailTouUsage, BucketRollup, BvTouBand,
  BvTariffBucketConfig, BvDistributionTreatmentInterval,
} from '../types/billValidationTypes';

// ─── Run summary (server persists this on run.tariff_result / run.tou_result) ──
// The server (nemAnalysisService.ts UsageRunSummary) shrunk the step-2/3 blob to
// this summary — the panels read the buckets themselves from the usage tables.
// Defined locally rather than imported from the server module (server import
// path doesn't resolve frontend-side); keep byte-identical to the server type.
// Exported in task 16 so the NEM Validator page + nemTypes can reuse it without
// redefining the shape (one source of truth for the frontend summary type).
export type UsageRunSummary = {
  run_id: string;
  days_processed: number;
  day_rows: number;
  interval_rows: number;
  skipped: string[];
  upToDate: boolean;
};

/** Coerce the stale-typed `tariff_result`/`tou_result` (declared as blob-shaped
 *  on the frontend NemAnalysisRun type) into the summary the server now stores.
 *  The frontend type hasn't been refreshed; this is the honest cast. */
const asSummary = (blob: unknown): UsageRunSummary | null => {
  if (!blob || typeof blob !== 'object') return null;
  const s = blob as Record<string, unknown>;
  if (typeof s.run_id !== 'string' && typeof s.days_processed !== 'number') return null;
  return {
    run_id: typeof s.run_id === 'string' ? s.run_id : '',
    days_processed: typeof s.days_processed === 'number' ? s.days_processed : 0,
    day_rows: typeof s.day_rows === 'number' ? s.day_rows : 0,
    interval_rows: typeof s.interval_rows === 'number' ? s.interval_rows : 0,
    skipped: Array.isArray(s.skipped) ? s.skipped.filter((x): x is string => typeof x === 'string') : [],
    upToDate: typeof s.upToDate === 'boolean' ? s.upToDate : false,
  };
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

/** Previous full calendar month as [from, to]. */
function defaultPeriod(): [string, string] {
  const now = new Date();
  const firstThis = new Date(now.getFullYear(), now.getMonth(), 1);
  const lastPrev = new Date(firstThis.getTime() - 24 * 60 * 60 * 1000);
  const firstPrev = new Date(lastPrev.getFullYear(), lastPrev.getMonth(), 1);
  const iso = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  return [iso(firstPrev), iso(lastPrev)];
}

const kwh = (n: number) => n.toLocaleString(undefined, { minimumFractionDigits: 3, maximumFractionDigits: 3 });
const money = (n: number | null | undefined): string =>
  n == null ? '—' : `$${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const num = (n: number | null | undefined, digits = 3): string =>
  n == null ? '—' : n.toLocaleString(undefined, { minimumFractionDigits: digits, maximumFractionDigits: digits });

const BAND_LABEL: Record<BvTouBand, string> = {
  superpeak: 'Super Peak', peak: 'Peak', shoulder: 'Shoulder', offpeak: 'Off Peak',
};

const STATUS_STYLE: Record<NemStepStatus, string> = {
  idle: 'bg-gray-100 text-gray-500',
  running: 'bg-blue-100 text-blue-700',
  done: 'bg-green-100 text-green-700',
  error: 'bg-red-100 text-red-700',
};

function StatusChip({ status, stale }: { status: NemStepStatus; stale?: boolean }) {
  return (
    <span className="flex items-center gap-1.5">
      <span className={cn('inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-semibold', STATUS_STYLE[status])}>
        {status === 'running' && <Loader2 className="h-3 w-3 animate-spin" />}
        {status === 'done' && <CheckCircle2 className="h-3 w-3" />}
        {status === 'error' && <AlertTriangle className="h-3 w-3" />}
        {status.toUpperCase()}
      </span>
      {stale && status === 'done' && (
        <span className="inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-semibold bg-amber-100 text-amber-700">
          <Timer className="h-3 w-3" /> STALE
        </span>
      )}
    </span>
  );
}

type StepCardProps = {
  index: number;
  title: string;
  subtitle: string;
  icon: React.ComponentType<{ className?: string }>;
  status: NemStepStatus;
  stale: boolean;
  error: string | null;
  at: string | null;
  actionLabel: string;
  onAction: () => void;
  /** Steps 2-3 only: reprocess days the RPC has already stamped. A plain re-run
   *  skips them and reports "up to date", which is wrong after a config change. */
  onRecalculate?: () => void;
  disabled: boolean;
  disabledHint?: string;
  children?: React.ReactNode;
};

function StepCard({
  index, title, subtitle, icon: Icon, status, stale, error, at,
  actionLabel, onAction, onRecalculate, disabled, disabledHint, children,
}: StepCardProps) {
  const [expanded, setExpanded] = useState(false);
  const hasContent = status === 'done' && children != null;

  return (
    <Card className={cn(status === 'error' && 'border-red-200')}>
      <CardHeader className="py-3 px-4">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3 min-w-0">
            <div className={cn(
              'flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-sm font-semibold',
              status === 'done' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500',
            )}>
              {index}
            </div>
            <Icon className="h-4 w-4 text-primary shrink-0" />
            <div className="min-w-0">
              <p className="font-semibold text-sm text-gray-900">{title}</p>
              <p className="text-xs text-gray-500 truncate">
                {subtitle}
                {at && <span className="text-gray-400"> · {new Date(at).toLocaleString('en-AU')}</span>}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <StatusChip status={status} stale={stale} />
            {/* Re-run on a finished step recalculates every day of the period.
                It used to skip days already processed, so after a tariff change
                it reported "up to date" and left the old prices in place, while
                a separate Recalculate button did the real work (site VEEE03KXUR2,
                Oct 2026: LVTOU -> UMBO never reached July/August). */}
            <Button
              variant={status === 'done' ? 'outline' : 'default'}
              size="sm"
              className="gap-1.5"
              onClick={status === 'done' && onRecalculate ? onRecalculate : onAction}
              disabled={disabled || status === 'running'}
              title={disabled ? disabledHint
                : status === 'done' && onRecalculate
                  ? 'Recalculate every day in the period against the current tariff and rates.'
                  : undefined}
            >
              {status === 'running'
                ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                : status === 'done' ? <RefreshCw className="h-3.5 w-3.5" /> : <PlayCircle className="h-3.5 w-3.5" />}
              {status === 'running' ? 'Running…' : status === 'done' ? `Re-run` : actionLabel}
            </Button>
            {hasContent && (
              <Button variant="ghost" size="sm" onClick={() => setExpanded(!expanded)} className="px-2">
                {expanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
              </Button>
            )}
          </div>
        </div>
        {error && (
          <div className="mt-2 flex items-start gap-2 rounded-md bg-red-50 px-3 py-2 text-xs text-red-700">
            <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5" />
            <span className="break-words">{error}</span>
          </div>
        )}
      </CardHeader>
      {hasContent && expanded && (
        <CardContent className="p-4 border-t bg-gray-50/40">{children}</CardContent>
      )}
    </Card>
  );
}

// ─── Step 1 panel: coverage ───────────────────────────────────────────────────

const QUALITY_LABELS: Record<string, string> = {
  A: 'Actual', E: 'Estimated', F: 'Final Sub', S: 'Substituted', V: 'Variable', N: 'Null',
};

function CoveragePanel({ coverage }: { coverage: StreamCoverage }) {
  const channelEntries = Object.entries(coverage.channels).sort();
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { label: 'Coverage', value: `${coverage.daysPresent}/${coverage.daysExpected} days`, warn: coverage.missingDates.length > 0 },
          { label: 'Total kWh (E)', value: coverage.totalKwh.toLocaleString(undefined, { maximumFractionDigits: 1 }) },
          { label: 'Interval', value: `${coverage.intervalMin} min${coverage.mixedIntervalLengths ? ' (mixed!)' : ''}`, warn: coverage.mixedIntervalLengths },
          { label: 'Fidelity', value: coverage.fidelity === 'raw' ? 'Raw intervals' : 'Daily rollup', warn: coverage.fidelity !== 'raw' },
        ].map(({ label, value, warn }) => (
          <Card key={label}>
            <CardContent className="py-2.5 px-3">
              <p className="text-xs text-gray-400">{label}</p>
              <p className={cn('text-sm font-semibold mt-0.5', warn ? 'text-amber-600' : 'text-gray-900')}>{value}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="flex flex-wrap gap-4 text-xs text-gray-600">
        <span>
          <span className="font-medium">Channels:</span>{' '}
          {channelEntries.length === 0 ? '—' : channelEntries.map(([c]) => c).join(', ')}
          {!channelEntries.some(([c]) => c.toUpperCase().startsWith('K') || c.toUpperCase().startsWith('Q')) && (
            <span className="text-amber-600"> (no K/Q — demand buckets will be blank)</span>
          )}
        </span>
        <span>
          <span className="font-medium">Providers:</span> {coverage.providers.join(', ') || '—'}
          {coverage.providers.length > 1 && <span className="text-amber-600"> (multi-provider overlap — verify)</span>}
        </span>
        <span>
          <span className="font-medium">Day quality:</span>{' '}
          {Object.entries(coverage.qualitySummary)
            .map(([f, n]) => `${QUALITY_LABELS[f] ?? f}: ${n}`)
            .join(' · ') || '—'}
        </span>
      </div>

      {coverage.fidelity === 'daily-rollup' && (
        <p className="rounded-md bg-amber-50 px-3 py-2 text-xs text-amber-700">
          Raw intervals are outside the retention window — using permanent daily rollups.
          Per-slot quality detail is unavailable for this period.
        </p>
      )}

      {coverage.missingDates.length > 0 && (
        <div className="rounded-md bg-amber-50 px-3 py-2">
          <p className="text-xs font-medium text-amber-700 mb-1">
            {coverage.missingDates.length} day{coverage.missingDates.length === 1 ? '' : 's'} with no interval data:
          </p>
          <div className="flex flex-wrap gap-1">
            {coverage.missingDates.map(d => (
              <span key={d} className="rounded bg-white px-1.5 py-0.5 text-xs text-amber-800 border border-amber-200">{d}</span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Step 2 panel: tariff structure (read from bv_distribution_tariff_usage) ──

// Task 16: RunSummary + TariffTab + TariffTabProps are exported so the NEM
// Validator page can reuse the SAME tariff rendering as the stepper (no drift,
// spec §6.7). The body is unchanged — the validator renders one TariffTab per
// matched site; getTreatmentPeak returning no data for an unconfigured treatment
// just means no demand row renders.
export function RunSummary({ summary }: { summary: UsageRunSummary }) {
  return (
    <div className="space-y-2">
      <p className="text-xs text-gray-500">
        {summary.upToDate
          ? 'Up to date — no new interval data to process'
          : `${summary.days_processed} day(s) processed · ${summary.day_rows} bucket rows` +
            (summary.interval_rows > 0 ? ` · ${summary.interval_rows} demand intervals` : '')}
      </p>
      {summary.skipped.length > 0 && (
        <div className="flex items-start gap-2 rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-800">
          <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
          <div>
            <p className="font-medium">Some buckets were skipped</p>
            <ul className="list-disc ml-4">
              {summary.skipped.map(s => <li key={s}>{s}</li>)}
            </ul>
          </div>
        </div>
      )}
    </div>
  );
}

export type TariffTabProps = {
  summary: UsageRunSummary;
  siteId: string;
  periodFrom: string;
  periodTo: string;
};

/**
 * Why a bucket shows the number it shows. The table is built from the CONFIGURED
 * bucket set left-joined onto results, so every state below gets a visible row —
 * before this, a bucket with no result rows was indistinguishable from a bucket
 * that does not exist, and an EA256 site rendered as a 2-bucket tariff when it
 * has 4 (Supply was dropped by the pipeline, Peak Demand by a broken lookup).
 */
type BucketState =
  | 'calculated'      // usage rows summed for the period
  | 'reduced'         // treatment bucket, figure came from bv_reduce_bucket
  | 'zero'            // configured and classifiable, nothing matched this period
  | 'no_vocabulary'   // day/time mappings absent — the RPC cannot classify it
  | 'error';          // the reduce call failed; message shown, never swallowed

type BucketRow = {
  cfg: BvTariffBucketConfig;
  state: BucketState;
  quantity: number | null;
  uom: string;
  rate: number | null;
  priceType: string | null;
  cost: number | null;
  stale: boolean;
  slotCount: number;
  /** Day rows behind a usage bucket — the expander reads these, no extra query. */
  days: BvDistributionTariffUsage[];
  rollup: BucketRollup | null;
  error: string | null;
};

const STATE_CHIP: Record<BucketState, { label: string; className: string } | null> = {
  calculated: null,
  reduced: null,
  zero: { label: 'NO USAGE', className: 'bg-gray-100 text-gray-500' },
  no_vocabulary: { label: 'NEEDS VOCABULARY', className: 'bg-amber-100 text-amber-700' },
  error: { label: 'ERROR', className: 'bg-red-100 text-red-700' },
};

/** `39.7570 c/kWh` — the unit is half the meaning, and it used to be dropped. */
const rateWithUnit = (rate: number | null, priceType: string | null): string => {
  if (rate == null) return '—';
  const n = rate.toLocaleString(undefined, { maximumFractionDigits: 4 });
  return priceType ? `${n} ${priceType}` : n;
};

const qtyWithUom = (q: number | null, uom: string): string =>
  q == null ? '—' : `${num(q)} ${uom}`;
export function TariffTab({ summary, siteId, periodFrom, periodTo }: TariffTabProps) {
  const [config, setConfig] = useState<BvTariffBucketConfig[]>([]);
  const [buckets, setBuckets] = useState<BvDistributionTariffUsage[]>([]);
  const [rollups, setRollups] = useState<Record<string, BucketRollup>>({});
  const [rollupErrors, setRollupErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Expander state: which bucket is open, and the treatment intervals fetched
  // for it (only treatment buckets need a second query — usage buckets expand
  // from day rows already in hand).
  const [openBucket, setOpenBucket] = useState<string | null>(null);
  const [intervals, setIntervals] = useState<Record<string, BvDistributionTreatmentInterval[]>>({});
  const [intervalsLoading, setIntervalsLoading] = useState<string | null>(null);

  useEffect(() => {
    let mounted = true;

    const load = async () => {
      try {
        setLoading(true);
        setError(null);

        const [cfgRes, bucketRes] = await Promise.all([
          usageService.getTariffConfig(siteId),
          usageService.getDistributionBuckets(siteId, periodFrom, periodTo),
        ]);
        if (!mounted) return;
        if (!cfgRes.success) { setError(cfgRes.error ?? 'Failed to load tariff config'); return; }
        if (!bucketRes.success) { setError(bucketRes.error ?? 'Failed to load buckets'); return; }

        const cfg = cfgRes.data ?? [];
        setConfig(cfg);
        setBuckets(bucketRes.data ?? []);

        // Treatment buckets are absent from the day table by design: their figure
        // is a reduction over the period. Every configured treatment bucket is
        // reduced — no hardcoded 'PEAK_DEMAND', so a tariff with two demand
        // buckets works unchanged and a tariff with none makes no doomed call.
        const treatmentBuckets = cfg.filter(c => c.treatment_code != null);
        const results = await Promise.all(treatmentBuckets.map(async c => ({
          key: c.bucket_key,
          res: await usageService.getTreatmentPeak(
            siteId, c.bucket_key, periodFrom, periodTo,
            c.treatment_kind === 'accumulate' ? 'accumulate' : 'interval', {},
          ),
        })));
        if (!mounted) return;

        const ok: Record<string, BucketRollup> = {};
        const bad: Record<string, string> = {};
        for (const { key, res } of results) {
          if (res.success && res.data) ok[key] = res.data;
          else bad[key] = res.error ?? 'Reduction failed';
        }
        setRollups(ok);
        setRollupErrors(bad);
      } catch (err) {
        if (mounted) setError(err instanceof Error ? err.message : 'Unknown error');
      } finally {
        if (mounted) setLoading(false);
      }
    };

    void load();
    return () => { mounted = false; };
  }, [siteId, periodFrom, periodTo]);

  // Config left-joined onto results. The day table stores sums by design, so
  // adding them up here is display aggregation of already-stored sums; treatment
  // figures are NOT reduced here — bv_reduce_bucket is the only reducer.
  const rows = useMemo<BucketRow[]>(() => {
    const byBucket = new Map<string, BvDistributionTariffUsage[]>();
    for (const b of buckets) {
      const list = byBucket.get(b.bucket_key);
      if (list) list.push(b);
      else byBucket.set(b.bucket_key, [b]);
    }

    return config.map(cfg => {
      const days = byBucket.get(cfg.bucket_key) ?? [];
      const rollup = rollups[cfg.bucket_key] ?? null;
      const rollupError = rollupErrors[cfg.bucket_key] ?? null;

      if (days.length > 0) {
        const quantity = days.reduce((sum, d) => sum + Number(d.quantity), 0);
        // A NULL cost anywhere means the total for this bucket is unknowable —
        // summing the non-null ones would present a partial figure as complete.
        const anyCostNull = days.some(d => d.cost == null);
        return {
          cfg,
          state: 'calculated' as BucketState,
          quantity,
          uom: days[0].uom,
          rate: days[0].rate,
          priceType: days[0].price_type,
          cost: anyCostNull ? null : days.reduce((sum, d) => sum + Number(d.cost ?? 0), 0),
          stale: days.some(d => d.is_stale),
          slotCount: days.reduce((sum, d) => sum + (d.slot_count ?? 0), 0),
          days,
          rollup: null,
          error: null,
        };
      }

      if (rollup) {
        return {
          cfg, state: 'reduced' as BucketState,
          quantity: rollup.quantity, uom: rollup.uom,
          rate: rollup.rate ?? cfg.price, priceType: rollup.price_type ?? cfg.price_type,
          cost: rollup.cost, stale: rollup.stale, slotCount: rollup.rows_considered,
          days: [], rollup, error: null,
        };
      }

      const base = {
        cfg, quantity: null, uom: cfg.is_daily ? 'days' : 'kWh',
        rate: cfg.price, priceType: cfg.price_type, cost: null,
        stale: false, slotCount: 0, days: [], rollup: null,
      };
      if (!cfg.has_vocabulary) return { ...base, state: 'no_vocabulary' as BucketState, error: null };
      if (rollupError) return { ...base, state: 'error' as BucketState, error: rollupError };
      return { ...base, state: 'zero' as BucketState, quantity: 0, error: null };
    });
  }, [config, buckets, rollups, rollupErrors]);

  const priced = rows.filter(r => r.cost != null);
  const totalCost = priced.reduce((sum, r) => sum + (r.cost ?? 0), 0);
  const header = config[0];

  // Config problems that make a number missing or unpriceable. Named here so the
  // user knows it is a configuration gap, not a calculation that failed.
  const warnings = [
    ...config.filter(c => !c.has_vocabulary)
      .map(c => `${c.bucket_label}: no day/time vocabulary mapping — cannot be classified`),
    ...config.filter(c => c.price != null && !c.cost_convertible)
      .map(c => `${c.bucket_label}: rate is ${c.price_type} — not convertible to a cost, quantity only`),
  ];

  const toggle = async (row: BucketRow) => {
    const key = row.cfg.bucket_key;
    if (openBucket === key) { setOpenBucket(null); return; }
    setOpenBucket(key);
    // Treatment buckets need their intervals; fetch once, then cache.
    if (row.state === 'reduced' && !intervals[key]) {
      setIntervalsLoading(key);
      // A lookback charge is set by the highest interval in the window ending at
      // the period's end (bv_treat_max_interval_lookback), not by the bill month
      // alone. Listing only the month put July's peak on top while the charge
      // came from March (site 172b8c7b, Ausgrid EA302, Oct 2026).
      const from = lookbackFrom(row.rollup, periodFrom, periodTo);
      const res = await usageService.getTreatmentIntervals(siteId, key, from, periodTo, 20);
      setIntervalsLoading(null);
      if (res.success) setIntervals(prev => ({ ...prev, [key]: res.data ?? [] }));
    }
  };

  return (
    <div className="space-y-3">
      <RunSummary summary={summary} />

      {loading && (
        <div className="flex items-center justify-center py-8 text-gray-400 gap-2">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading buckets…
        </div>
      )}

      {error && (
        <div className="flex items-start gap-2 rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">
          <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
          <span>{error}</span>
        </div>
      )}

      {!loading && !error && config.length === 0 && (
        <Card>
          <CardContent className="flex flex-col items-center py-12 gap-2 text-gray-400">
            <Network className="h-8 w-8" />
            <p>No network tariff configured for this site</p>
            <p className="text-xs">Set a distributor and tariff code on the site to price its usage.</p>
          </CardContent>
        </Card>
      )}

      {!loading && !error && config.length > 0 && (
        <Card>
          {header && (
            <CardHeader className="py-3 px-4 border-b">
              <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1 text-xs">
                <span className="font-semibold text-sm text-gray-900">{header.distributor}</span>
                <span className="text-gray-400">·</span>
                <span className="font-mono text-gray-700">{header.tariff_code}</span>
                {header.tariff_name && (
                  <>
                    <span className="text-gray-400">·</span>
                    <span className="text-gray-600">{header.tariff_name}</span>
                  </>
                )}
                <span className="text-gray-400">·</span>
                <span className="text-gray-500">{config.length} configured bucket{config.length === 1 ? '' : 's'}</span>
              </div>
            </CardHeader>
          )}

          <CardContent className="p-0">
            <table className="w-full text-xs">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="py-2 px-3 text-left font-medium text-gray-500">Bucket</th>
                  <th className="py-2 px-3 text-left font-medium text-gray-500">Window</th>
                  <th className="py-2 px-3 text-right font-medium text-gray-500">Qty</th>
                  <th className="py-2 px-3 text-right font-medium text-gray-500">Rate</th>
                  <th className="py-2 px-3 text-right font-medium text-gray-500">Cost</th>
                  <th className="py-2 px-3 w-8" />
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {rows.map(r => {
                  const chip = STATE_CHIP[r.state];
                  const isOpen = openBucket === r.cfg.bucket_key;
                  const expandable = r.days.length > 0 || r.state === 'reduced';
                  return (
                    <>
                      <tr
                        key={r.cfg.bucket_key}
                        className={cn(
                          'hover:bg-gray-50',
                          r.state === 'reduced' && 'bg-amber-50/40',
                          expandable && 'cursor-pointer',
                        )}
                        onClick={expandable ? () => void toggle(r) : undefined}
                      >
                        <td className="py-2 px-3 text-gray-700">
                          <span className="font-medium">{r.cfg.bucket_label}</span>
                          {r.stale && (
                            <span className="ml-1.5 inline-flex items-center gap-0.5 rounded-full px-1.5 py-0.5 text-xs font-semibold bg-amber-100 text-amber-700">
                              <Timer className="h-3 w-3" />STALE
                            </span>
                          )}
                          {chip && (
                            <span className={cn('ml-1.5 inline-flex rounded-full px-1.5 py-0.5 text-xs font-semibold', chip.className)}>
                              {chip.label}
                            </span>
                          )}
                          {r.state === 'reduced' && r.rollup && (
                            <p className="text-xs text-gray-400 font-normal">
                              {r.rollup.treatment_label ?? r.rollup.basis}
                              {r.rollup.applicable_duration ? ` · ${r.rollup.applicable_duration}` : ''}
                              {` · ${r.rollup.rows_considered} interval${r.rollup.rows_considered === 1 ? '' : 's'}`}
                            </p>
                          )}
                          {r.cfg.is_daily && r.state === 'calculated' && (
                            <p className="text-xs text-gray-400 font-normal">
                              {r.days.length} chargeable day{r.days.length === 1 ? '' : 's'}
                            </p>
                          )}
                          {r.error && <p className="text-xs text-red-600 font-normal">{r.error}</p>}
                        </td>
                        <td className="py-2 px-3 text-gray-500">{r.cfg.window_label}</td>
                        <td className={cn('py-2 px-3 text-right', r.state === 'zero' ? 'text-gray-400' : 'text-gray-800')}>
                          {qtyWithUom(r.quantity, r.uom)}
                        </td>
                        <td className="py-2 px-3 text-right text-gray-600">
                          {rateWithUnit(r.rate, r.priceType)}
                        </td>
                        <td className={cn('py-2 px-3 text-right font-medium', r.cost == null ? 'text-gray-400' : 'text-gray-900')}
                            title={r.cost == null && !r.cfg.cost_convertible
                              ? `Rate unit ${r.cfg.price_type} cannot be converted to a cost`
                              : undefined}>
                          {money(r.cost)}
                        </td>
                        <td className="py-2 px-3 text-gray-400">
                          {expandable && (isOpen
                            ? <ChevronUp className="h-3.5 w-3.5" />
                            : <ChevronDown className="h-3.5 w-3.5" />)}
                        </td>
                      </tr>

                      {isOpen && (
                        <tr key={`${r.cfg.bucket_key}-detail`} className="bg-gray-50/60">
                          <td colSpan={6} className="px-3 py-2">
                            <BucketDetail
                              row={r}
                              intervals={intervals[r.cfg.bucket_key] ?? []}
                              loading={intervalsLoading === r.cfg.bucket_key}
                            />
                          </td>
                        </tr>
                      )}
                    </>
                  );
                })}
              </tbody>
              <tfoot className="border-t bg-gray-50">
                <tr>
                  <td className="py-2 px-3 font-medium text-gray-700" colSpan={4}>
                    Total
                    {priced.length < rows.length && (
                      <span className="ml-1.5 font-normal text-amber-700">
                        ({priced.length} of {rows.length} bucket{rows.length === 1 ? '' : 's'} priced)
                      </span>
                    )}
                  </td>
                  <td className="py-2 px-3 text-right font-semibold text-gray-900">{money(totalCost)}</td>
                  <td />
                </tr>
              </tfoot>
            </table>
          </CardContent>

          {warnings.length > 0 && (
            <div className="border-t bg-amber-50 px-3 py-2 text-xs text-amber-800">
              <p className="flex items-center gap-1.5 font-medium">
                <AlertTriangle className="h-3.5 w-3.5" /> Tariff configuration
              </p>
              <ul className="list-disc ml-5 mt-1 space-y-0.5">
                {warnings.map(w => <li key={w}>{w}</li>)}
              </ul>
            </div>
          )}
        </Card>
      )}
    </div>
  );
}

/** Per-bucket drill-down: the days behind a usage figure, or the intervals that
 *  set a demand charge. Answers "why is this number this number" in one click. */
function BucketDetail({
  row, intervals, loading,
}: { row: BucketRow; intervals: BvDistributionTreatmentInterval[]; loading: boolean }) {
  if (row.state === 'reduced') {
    if (loading) {
      return (
        <p className="flex items-center gap-2 py-2 text-gray-400">
          <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading intervals…
        </p>
      );
    }
    if (intervals.length === 0) {
      return <p className="py-2 text-gray-400">No intervals recorded for this bucket in the period.</p>;
    }
    const lookback = row.rollup?.basis === 'bv_treat_max_interval_lookback';
    return (
      <div className="space-y-1">
        <p className="text-gray-500">
          Highest intervals first — the top row is what set the charge
          {row.rollup?.treatment_label ? ` (${row.rollup.treatment_label})` : ''}
          {lookback ? `, across the ${row.rollup?.applicable_duration ?? '12 months'} to the end of this period` : ''}.
        </p>
        <table className="w-full">
          <thead className="text-gray-500">
            <tr>
              <th className="py-1 pr-3 text-left font-medium">Date</th>
              <th className="py-1 pr-3 text-left font-medium">Start</th>
              <th className="py-1 pr-3 text-right font-medium">kWh</th>
              <th className="py-1 pr-3 text-right font-medium">Gross</th>
              <th className="py-1 pr-3 text-right font-medium">Threshold</th>
              <th className="py-1 text-right font-medium">Chargeable</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {intervals.map(iv => (
              <tr key={iv.id}>
                <td className="py-1 pr-3 text-gray-700">{iv.usage_date}</td>
                <td className="py-1 pr-3 text-gray-700">{iv.interval_start.slice(0, 5)}</td>
                <td className="py-1 pr-3 text-right text-gray-700">{num(iv.kwh, 4)}</td>
                <td className="py-1 pr-3 text-right text-gray-700">{num(iv.power_gross)} {iv.uom}</td>
                <td className="py-1 pr-3 text-right text-gray-500">{iv.threshold_kw == null ? '—' : num(iv.threshold_kw)}</td>
                <td className="py-1 text-right font-medium text-gray-900">{num(iv.power_chargeable)} {iv.uom}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  if (row.days.length === 0) {
    return <p className="py-2 text-gray-400">Nothing stored for this bucket in the period.</p>;
  }

  return (
    <table className="w-full">
      <thead className="text-gray-500">
        <tr>
          <th className="py-1 pr-3 text-left font-medium">Date</th>
          <th className="py-1 pr-3 text-right font-medium">Qty</th>
          <th className="py-1 pr-3 text-right font-medium">{row.cfg.is_daily ? 'Basis' : 'Slots'}</th>
          <th className="py-1 pr-3 text-right font-medium">Cost</th>
          <th className="py-1 text-left font-medium">Fidelity</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-gray-200">
        {row.days.map(d => (
          <tr key={d.id}>
            <td className="py-1 pr-3 text-gray-700">{d.usage_date}</td>
            <td className="py-1 pr-3 text-right text-gray-700">{num(Number(d.quantity))} {d.uom}</td>
            <td className="py-1 pr-3 text-right text-gray-500">{d.slot_count ?? d.agg_method}</td>
            <td className="py-1 pr-3 text-right text-gray-700">{money(d.cost)}</td>
            <td className="py-1 text-gray-500">
              {d.source_fidelity}
              {d.is_stale && <span className="ml-1 text-amber-700">stale</span>}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
// ─── Step 3 panel: TOU bands per NMI (read from bv_retail_tou_usage) ──────────

type TouPanelProps = {
  summary: UsageRunSummary;
  siteId: string;
  periodFrom: string;
  periodTo: string;
  runId: string;
};

function TouPanel({ summary, siteId, periodFrom, periodTo, runId }: TouPanelProps) {
  const [bands, setBands] = useState<BvRetailTouUsage[]>([]);
  const [entries, setEntries] = useState<TouByNmi[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [slotsLoading, setSlotsLoading] = useState<string | null>(null);

  useEffect(() => {
    let mounted = true;

    const load = async () => {
      try {
        setLoading(true);
        setError(null);
        const res = await usageService.getRetailBands(siteId, periodFrom, periodTo);
        if (!mounted) return;
        if (!res.success) { setError(res.error ?? 'Failed to load bands'); return; }
        const rows = res.data ?? [];
        setBands(rows);
        setEntries(groupBandsByNmi(rows));
      } catch (err) {
        if (mounted) setError(err instanceof Error ? err.message : 'Unknown error');
      } finally {
        if (mounted) setLoading(false);
      }
    };

    void load();
    return () => { mounted = false; };
  }, [siteId, periodFrom, periodTo]);

  // Priced band rollup. Migration 079 populates rate + cost on bv_retail_tou_usage
  // (it wrote neither before, so the panel could only ever show kWh). Rate comes
  // from the contract's bv_contract_rate row for the day; a band with no
  // resolvable rate keeps NULL cost and is counted, never defaulted to zero.
  // Consumption and export are separate lines (see rollupRetailBands).
  const bandRollup = useMemo(() => rollupRetailBands(bands), [bands]);

  const pricedBands = bandRollup.filter(b => b.cost != null);
  const bandTotal = pricedBands.reduce((sum, b) => sum + (b.cost ?? 0), 0);
  const retailer = bands[0]?.retailer_name ?? null;
  const noContract = retailer === 'Unknown';

  // Per-slot drilldown: regenerate server-side from intervals (not persisted).
  // The bands table has no slot detail — this is the existing "How calculated"
  // button, now operating on the synthetic entries built from the bands.
  const handleLoadSlots = async (targetNmi: string) => {
    setSlotsLoading(targetNmi);
    try {
      const entry = entries.find(n => n.nmi === targetNmi);
      if (!entry) return;
      const updatedStreams = await Promise.all(entry.streams.map(async s => {
        const res = await nemAnalysisService.getTouSlots(runId, targetNmi, s.suffix);
        return res.success ? { ...s, slots: res.data ?? [] } : s;
      }));
      setEntries(prev => prev.map(n => n.nmi === targetNmi ? { ...n, streams: updatedStreams } : n));
    } finally {
      setSlotsLoading(null);
    }
  };

  return (
    <div className="space-y-3">
      <RunSummary summary={summary} />

      {loading && (
        <div className="flex items-center justify-center py-8 text-gray-400 gap-2">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading bands…
        </div>
      )}

      {error && (
        <div className="flex items-start gap-2 rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">
          <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
          <span>{error}</span>
        </div>
      )}

      {!loading && !error && entries.length === 0 && (
        <Card>
          <CardContent className="flex flex-col items-center py-12 gap-2 text-gray-400">
            <Zap className="h-8 w-8" />
            <p>No retail TOU bands for this period</p>
          </CardContent>
        </Card>
      )}

      {/* No current contract ⇒ retailer 'Unknown' ⇒ no band windows match ⇒ every
          slot lands in the off-peak residual. Say so, rather than presenting a
          residual-only split as a genuine TOU classification. */}
      {!loading && !error && noContract && (
        <div className="flex items-start gap-2 rounded-md bg-amber-50 border border-amber-200 px-3 py-2 text-sm text-amber-800">
          <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
          <span>
            <span className="font-medium">No current retail contract for this site.</span>{' '}
            Retailer resolved to <span className="font-mono">Unknown</span>, so no TOU
            band windows matched and all usage is shown as the off-peak residual — and
            with no contract there are no rates to price it.
          </span>
        </div>
      )}

      {!loading && !error && bandRollup.length > 0 && (
        <Card>
          <CardHeader className="py-3 px-4 border-b">
            <p className="font-semibold text-sm text-gray-900">Charges</p>
            <p className="text-xs text-gray-500">
              {retailer ?? '—'} contract rates applied per band
            </p>
          </CardHeader>
          <CardContent className="p-0">
            <table className="w-full text-xs">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="py-2 px-3 text-left font-medium text-gray-500">Band</th>
                  <th className="py-2 px-3 text-right font-medium text-gray-500">Qty</th>
                  <th className="py-2 px-3 text-right font-medium text-gray-500">Rate</th>
                  <th className="py-2 px-3 text-right font-medium text-gray-500">Cost</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {bandRollup.map(b => (
                  <tr key={`${b.channel}:${b.band}`} className="hover:bg-gray-50">
                    <td className="py-2 px-3 text-gray-700 font-medium">
                      {b.channel === 'export' ? `Feed-in ${BAND_LABEL[b.band]}` : BAND_LABEL[b.band]}
                    </td>
                    <td className="py-2 px-3 text-right text-gray-800">{kwh(b.qty)} <span className="text-gray-400">kWh</span></td>
                    <td className="py-2 px-3 text-right text-gray-600">{rateWithUnit(b.rate, 'c/kWh')}</td>
                    <td className={cn('py-2 px-3 text-right font-medium', b.cost == null ? 'text-gray-400' : 'text-gray-900')}
                        title={b.cost == null ? 'No contract rate resolves for this band in this period' : undefined}>
                      {money(b.cost)}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="border-t bg-gray-50">
                <tr>
                  <td className="py-2 px-3 font-medium text-gray-700" colSpan={3}>
                    Total
                    {pricedBands.length < bandRollup.length && (
                      <span className="ml-1.5 font-normal text-amber-700">
                        ({pricedBands.length} of {bandRollup.length} band{bandRollup.length === 1 ? '' : 's'} priced)
                      </span>
                    )}
                  </td>
                  <td className="py-2 px-3 text-right font-semibold text-gray-900">{money(bandTotal)}</td>
                </tr>
              </tfoot>
            </table>
          </CardContent>
        </Card>
      )}

      {!loading && !error && entries.map(entry => {
        const hasSlots = entry.streams.some(s => s.slots && s.slots.length > 0);
        return (
          <Card key={entry.nmi}>
            <CardHeader className="py-3 px-4">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div>
                  <p className="font-semibold text-sm text-gray-900">{entry.nmi}</p>
                  <p className="text-xs text-gray-500">
                    {entry.retailer} · {entry.state} · {entry.meterCount} {entry.meterCount === 1 ? 'meter' : 'meters'}
                  </p>
                </div>
                {!hasSlots && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-xs gap-1"
                    disabled={slotsLoading === entry.nmi}
                    onClick={() => void handleLoadSlots(entry.nmi)}
                  >
                    {slotsLoading === entry.nmi ? <Loader2 className="h-3 w-3 animate-spin" /> : null}
                    Load per-slot detail
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent className="p-0 border-t">
              <table className="w-full text-xs">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="py-2 px-3 text-left font-medium text-gray-500">Suffix</th>
                    <th className="py-2 px-3 text-right font-medium text-gray-500">Peak (kWh)</th>
                    <th className="py-2 px-3 text-right font-medium text-gray-500">Shoulder (kWh)</th>
                    <th className="py-2 px-3 text-right font-medium text-gray-500">Off Peak (kWh)</th>
                    <th className="py-2 px-3 text-right font-medium text-gray-500">Total (kWh)</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {entry.streams.map(s => (
                    <tr key={`${s.suffix}-${s.meterSerial}`} className="hover:bg-gray-50">
                      <td className="py-2 px-3 text-gray-700">{s.suffix}</td>
                      <td className="py-2 px-3 text-right text-gray-800">{kwh(s.peak_kwh)}</td>
                      <td className="py-2 px-3 text-right text-gray-800">{kwh(s.shoulder_kwh)}</td>
                      <td className="py-2 px-3 text-right text-gray-800">{kwh(s.offpeak_kwh)}</td>
                      <td className="py-2 px-3 text-right font-medium text-gray-900">{kwh(s.total_kwh)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent>
            <TouCalculationCard entry={entry} />
          </Card>
        );
      })}

      {bands.some(b => b.is_stale) && (
        <div className="flex items-start gap-2 rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-800">
          <Timer className="h-4 w-4 shrink-0 mt-0.5" />
          <span>Some bands are flagged stale — re-run Step 3 to refresh.</span>
        </div>
      )}
    </div>
  );
}

/** Group retail band rows (per date) into the per-NMI/per-suffix shape the
 *  existing TouPanel layout expects. Quantities are summed across the period —
 *  the bands table stores per-day sums, so this is display aggregation of
 *  already-stored sums, not re-reducing intervals. */
function groupBandsByNmi(bands: BvRetailTouUsage[]): TouByNmi[] {
  const byNmi = new Map<string, { retailer: string; state: string; suffixes: Map<string, { peak: number; shoulder: number; offpeak: number }> }>();
  for (const b of bands) {
    let nmi = byNmi.get(b.nmi);
    if (!nmi) {
      nmi = { retailer: b.retailer_name, state: b.state, suffixes: new Map() };
      byNmi.set(b.nmi, nmi);
    }
    let suf = nmi.suffixes.get(b.nmi_suffix);
    if (!suf) { suf = { peak: 0, shoulder: 0, offpeak: 0 }; nmi.suffixes.set(b.nmi_suffix, suf); }
    // Super Peak (migration 144) is shown with Peak in this three-column panel;
    // the bill keeps it separate in super_peak_kwh.
    if (b.band === 'peak' || b.band === 'superpeak') suf.peak += Number(b.quantity);
    else if (b.band === 'shoulder') suf.shoulder += Number(b.quantity);
    else if (b.band === 'offpeak') suf.offpeak += Number(b.quantity);
  }
  return Array.from(byNmi, ([nmi, v]) => {
    const streams = Array.from(v.suffixes, ([suffix, s]) => ({
      suffix,
      meterSerial: '',
      peak_kwh: s.peak,
      shoulder_kwh: s.shoulder,
      offpeak_kwh: s.offpeak,
      total_kwh: s.peak + s.shoulder + s.offpeak,
    }));
    return { nmi, retailer: v.retailer, state: v.state, meterCount: streams.length, streams };
  });
}

// ─── Main stepper ─────────────────────────────────────────────────────────────

type Props = {
  siteId: string;
  orgId: string;
  nmi?: string | null;
};

export function NemAnalysisStepper({ siteId, orgId, nmi }: Props) {
  const [defFrom, defTo] = defaultPeriod();
  const [periodFrom, setPeriodFrom] = useState(defFrom);
  const [periodTo, setPeriodTo] = useState(defTo);
  const [run, setRun] = useState<NemAnalysisRun | null>(null);
  const [initialising, setInitialising] = useState(false);
  const [pageError, setPageError] = useState<string | null>(null);

  // Manual NEM12/13 upload (testing) — moved here from Bill Run: this is
  // site-wide meter data, not tied to a specific bill period, so NEM Analysis
  // (which is per-site) is the more intuitive home for it.
  const [nemFile, setNemFile] = useState<File | null>(null);
  const [nemProvider, setNemProvider] = useState<'mondo' | 'powermetric'>('powermetric');
  const [nemUploading, setNemUploading] = useState(false);
  const [nemUploadResult, setNemUploadResult] = useState<{ intervalsInserted: number; nmisMatched: string[]; nmisUnmatched: string[] } | null>(null);
  const [nemUploadError, setNemUploadError] = useState<string | null>(null);

  // Bill's-period convenience selector (fills periodFrom/To from an existing bill run).
  const [bills, setBills] = useState<BvBill[]>([]);
  const [selectedBillId, setSelectedBillId] = useState('');

  // Fetch existing run for the chosen period (no auto-create).
  useEffect(() => {
    let mounted = true;
    setRun(null);
    if (!periodFrom || !periodTo || periodFrom > periodTo) return;
    nemAnalysisService.getRunForPeriod(siteId, orgId, periodFrom, periodTo).then(res => {
      if (!mounted) return;
      if (res.success) setRun(res.data ?? null);
    });
    return () => { mounted = false; };
  }, [siteId, orgId, periodFrom, periodTo]);

  // Bills for step 4 selector.
  useEffect(() => {
    let mounted = true;
    billService.listBySite(siteId).then(res => {
      if (mounted && res.success) setBills(res.data ?? []);
    });
    return () => { mounted = false; };
  }, [siteId]);

  // Pre-select the bill matching the period.
  useEffect(() => {
    if (selectedBillId || bills.length === 0) return;
    const match = bills.find(b => b.bill_from === periodFrom && b.bill_to === periodTo);
    if (match) setSelectedBillId(match.id);
  }, [bills, periodFrom, periodTo, selectedBillId]);

  const usePeriodFromBill = (billId: string) => {
    const b = bills.find(x => x.id === billId);
    if (b) {
      setPeriodFrom(b.bill_from);
      setPeriodTo(b.bill_to);
      setSelectedBillId(b.id);
    }
  };

  const ensureRun = useCallback(async (): Promise<NemAnalysisRun | null> => {
    if (run) return run;
    setInitialising(true);
    setPageError(null);
    const res = await nemAnalysisService.getOrCreateRun(siteId, orgId, periodFrom, periodTo);
    setInitialising(false);
    if (!res.success || !res.data) {
      setPageError(res.error ?? 'Failed to create analysis run');
      return null;
    }
    setRun(res.data);
    return res.data;
  }, [run, siteId, orgId, periodFrom, periodTo]);

  // `force` only applies to steps 2-3: it reprocesses days the RPCs have already
  // stamped. Step 1 is a pure read, so it has nothing to force.
  const doStep = async (step: 'data' | 'tariff' | 'tou', force = false) => {
    const current = await ensureRun();
    if (!current) return;
    setPageError(null);
    // Optimistic running state so the chip flips immediately.
    setRun(prev => prev ? { ...prev, [`${step}_status`]: 'running' } as NemAnalysisRun : prev);
    const call = step === 'data'
      ? nemAnalysisService.loadData(current.id)
      : step === 'tariff'
        ? nemAnalysisService.runTariff(current.id, force)
        : nemAnalysisService.runTou(current.id, force);
    const res = await call;
    if (res.success && res.data) {
      setRun(res.data);
    } else {
      setPageError(res.error ?? `Step ${step} failed`);
      const refreshed = await nemAnalysisService.getRun(current.id);
      if (refreshed.success && refreshed.data) setRun(refreshed.data);
    }
  };

  const handleNemUpload = async () => {
    if (!nemFile) return;
    setNemUploading(true);
    setNemUploadResult(null);
    setNemUploadError(null);
    try {
      const formData = new FormData();
      formData.append('nemFile', nemFile);
      formData.append('provider', nemProvider);
      const res = await fetch(`${import.meta.env.VITE_API_URL ?? 'http://localhost:3001'}/api/bill-validation/nem/upload-to-db`, {
        method: 'POST',
        headers: { 'x-api-key': 'scopingai' },
        body: formData,
      });
      const json = await res.json() as { success: boolean; error?: string; data?: { intervalsInserted: number; nmisMatched: string[]; nmisUnmatched: string[] } };
      if (!json.success) throw new Error(json.error ?? 'Upload failed');
      setNemUploadResult(json.data ?? { intervalsInserted: 0, nmisMatched: [], nmisUnmatched: [] });
      setNemFile(null);
    } catch (err) {
      setNemUploadError(err instanceof Error ? err.message : 'Upload failed');
    } finally {
      setNemUploading(false);
    }
  };

  const dataStatus: NemStepStatus = run?.data_status ?? 'idle';
  const tariffStatus: NemStepStatus = run?.tariff_status ?? 'idle';
  const touStatus: NemStepStatus = run?.tou_status ?? 'idle';
  const stale = run?.is_stale ?? false;

  return (
    <div className="space-y-4">
      {/* Period selection — drives Steps 1-3 (meter data / tariff / retailer TOU) only. */}
      <Card>
        <CardHeader className="py-3 px-4">
          <div className="flex items-end gap-3 flex-wrap">
            <div>
              <Label className="text-xs text-gray-500">Period from</Label>
              <Input type="date" value={periodFrom} onChange={e => setPeriodFrom(e.target.value)} className="h-9 w-40" />
            </div>
            <div>
              <Label className="text-xs text-gray-500">Period to</Label>
              <Input type="date" value={periodTo} onChange={e => setPeriodTo(e.target.value)} className="h-9 w-40" />
            </div>
            {bills.length > 0 && (
              <div>
                <Label className="text-xs text-gray-500">…or use a bill's period</Label>
                <Select value={selectedBillId} onValueChange={usePeriodFromBill}>
                  <SelectTrigger className="h-9 w-64">
                    <SelectValue placeholder="Select bill" />
                  </SelectTrigger>
                  <SelectContent>
                    {bills.map(b => (
                      <SelectItem key={b.id} value={b.id}>
                        {b.bill_from} → {b.bill_to}{b.actual_total != null ? ` · $${b.actual_total.toLocaleString()}` : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <p className="w-full text-xs text-gray-400">
              NMI {nmi ?? '—'} · applies to Interval Data, Network Tariff Charges and Retail TOU Charges below.
            </p>
          </div>
        </CardHeader>
      </Card>

      {/* Manual NEM upload (testing) — moved here from Bill Run since it's site-wide meter data. */}
      <Card className="border border-dashed border-blue-200 bg-blue-50/30 shadow-none">
        <CardHeader className="pb-2">
          <p className="text-sm text-blue-800 font-semibold flex items-center gap-2">
            <Upload className="h-4 w-4" />
            Upload NEM File for Testing
          </p>
          <p className="text-xs text-blue-600 mt-0.5">
            Upload a local NEM12/NEM13 file directly into the database — skips GCS sync. Use this to test auto-populate without needing cloud setup.
          </p>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex flex-wrap items-center gap-3">
            <input
              type="file"
              accept=".csv,.txt,.zip"
              className="text-sm text-gray-700 file:mr-3 file:py-1.5 file:px-3 file:rounded-lg file:border file:border-gray-300 file:text-sm file:bg-white file:text-gray-700 hover:file:bg-gray-50 cursor-pointer"
              onChange={(e) => setNemFile(e.target.files?.[0] ?? null)}
            />
            <select
              value={nemProvider}
              onChange={(e) => setNemProvider(e.target.value as 'mondo' | 'powermetric')}
              className="text-sm border border-gray-300 rounded-lg px-3 py-1.5 bg-white text-gray-700"
            >
              <option value="powermetric">Powermetric</option>
              <option value="mondo">Mondo</option>
            </select>
            <Button
              size="sm"
              onClick={() => void handleNemUpload()}
              disabled={!nemFile || nemUploading}
              className="gap-2"
            >
              {nemUploading
                ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                : <Upload className="h-3.5 w-3.5" />}
              {nemUploading ? 'Uploading…' : 'Upload to DB'}
            </Button>
          </div>

          {nemUploadResult && (
            <div className="rounded-lg border border-green-200 bg-green-50 px-3 py-2 text-xs text-green-800 space-y-1">
              <p className="font-semibold">Upload complete</p>
              <p>{nemUploadResult.intervalsInserted} interval rows inserted into bv_nem_interval</p>
              {nemUploadResult.nmisMatched.length > 0 && <p>NMIs matched: {nemUploadResult.nmisMatched.join(', ')}</p>}
              {nemUploadResult.nmisUnmatched.length > 0 && (
                <p className="text-amber-700">NMIs not found in system: {nemUploadResult.nmisUnmatched.join(', ')}</p>
              )}
              <p className="text-green-700 font-medium mt-1">Now run Step 1 (Interval Data) below to pick up these intervals.</p>
            </div>
          )}

          {nemUploadError && (
            <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700 flex items-center gap-1.5">
              <X className="h-3.5 w-3.5 flex-shrink-0" />
              {nemUploadError}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Stale banner */}
      {stale && (
        <div className="flex items-start gap-2 rounded-md bg-amber-50 border border-amber-200 px-3 py-2 text-sm text-amber-800">
          <Timer className="h-4 w-4 shrink-0 mt-0.5" />
          <span>
            <span className="font-medium">Interval data changed since this analysis was calculated.</span>{' '}
            {run?.stale_reason} — re-run Step 1 to refresh.
          </span>
        </div>
      )}

      {pageError && (
        <div className="flex items-start gap-2 rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">
          <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
          <span>{pageError}</span>
        </div>
      )}

      {/* Step 1 — Meter data */}
      <StepCard
        index={1}
        title="Interval Data"
        subtitle={run?.data_result
          ? `${run.data_result.daysPresent}/${run.data_result.daysExpected} days · ${run.data_result.totalKwh.toLocaleString(undefined, { maximumFractionDigits: 1 })} kWh · ${run.data_result.providers.join(', ') || 'no provider'}`
          : 'Check what interval data is stored for this period'}
        icon={Database}
        status={initialising ? 'running' : dataStatus}
        stale={stale}
        error={run?.data_error ?? null}
        at={run?.data_at ?? null}
        actionLabel="Check Coverage"
        onAction={() => void doStep('data')}
        disabled={!periodFrom || !periodTo || periodFrom > periodTo}
        disabledHint="Pick a valid period first"
      >
        {run?.data_result ? (
          <div className="space-y-2">
            {/* This step reads bv_nem_interval only — it never touches GCS. Saying
                so kills the "why is it called GCS / why are days missing" question. */}
            <p className="text-xs text-gray-500">
              Reads interval data already stored for this site. Data arrives from the
              daily GCS sync (or a manual Sync GCS from the Bill Validation dashboard),
              or the upload box above — this step does not fetch anything new.
            </p>
            <CoveragePanel coverage={run.data_result} />
          </div>
        ) : null}
      </StepCard>

      {/* Step 2 — Tariff structure */}
      <StepCard
        index={2}
        title="Network Tariff Charges"
        subtitle={(() => {
          const s = asSummary(run?.tariff_result);
          if (!s) return 'Split usage into the network tariff\'s buckets and price them';
          return s.upToDate
            ? 'Up to date — no new interval data to process'
            : `${s.days_processed} day(s) processed · ${s.day_rows} bucket rows`;
        })()}
        icon={Network}
        status={tariffStatus}
        stale={stale}
        error={run?.tariff_error ?? null}
        at={run?.tariff_at ?? null}
        actionLabel="Calculate"
        onAction={() => void doStep('tariff')}
        onRecalculate={() => void doStep('tariff', true)}
        disabled={dataStatus !== 'done'}
        disabledHint="Run Step 1 (Interval Data) first"
      >
        {(() => {
          const s = asSummary(run?.tariff_result);
          return s ? (
            <TariffTab summary={s} siteId={siteId} periodFrom={periodFrom} periodTo={periodTo} />
          ) : null;
        })()}
      </StepCard>

      {/* Step 3 — Retailer TOU */}
      <StepCard
        index={3}
        title="Retail TOU Charges"
        subtitle={(() => {
          const s = asSummary(run?.tou_result);
          if (!s) return 'Classify every interval into Peak / Shoulder / Off-Peak';
          return s.upToDate
            ? 'Up to date — no new interval data to process'
            : `${s.days_processed} day(s) processed · ${s.day_rows} band rows`;
        })()}
        icon={Zap}
        status={touStatus}
        stale={stale}
        error={run?.tou_error ?? null}
        at={run?.tou_at ?? null}
        actionLabel="Calculate"
        onAction={() => void doStep('tou')}
        onRecalculate={() => void doStep('tou', true)}
        disabled={tariffStatus !== 'done'}
        disabledHint="Run Step 2 (Network Tariff Charges) first"
      >
        {(() => {
          const s = asSummary(run?.tou_result);
          return s && run ? (
            <TouPanel
              summary={s}
              siteId={siteId}
              periodFrom={periodFrom}
              periodTo={periodTo}
              runId={run.id}
            />
          ) : null;
        })()}
      </StepCard>
    </div>
  );
}
